import { action, computed, makeObservable, observable } from 'mobx'
import { Maybe } from '@Types'
import { SearchOptionType } from '@App/enum/search-option-type'
import { RequiredCriteria } from '@App/enum/required-criteria'
import { SelectOptionHide } from '@App/stores/search/models/select-option-hide'
import { SearchCriterion } from '@App/stores/search/models/search-criterion'
import { v4 as uuidv4 } from 'uuid'
import { EXACT_MATCH_SUFFIX } from '@App/helpers/map-criteria-to-object'

export default class SearchCriteriaStore<T extends object> {
    private _searchCriteria: Array<SearchCriterion<T>> = []
    private primaryFilterNames: string[]
    private readonly _criteriaOptions: Array<SelectOptionHide<T>> = []
    private readonly maxCriteria: number
    private readonly searchOptionType: SearchOptionType
    private readonly requiredCriteria: RequiredCriteria
    private _isUniqueSelected = false
    private readonly _defaultSearchCriteriaLength: number

    constructor(
        searchCriteriaArray: Array<SearchCriterion<T>>,
        criteriaOptionsArray: Array<SelectOptionHide<T>>,
        searchOptionType: SearchOptionType,
        requiredCriteria: RequiredCriteria,
        maxCriteria = 6,
        primaryFilterNames?: string[]
    ) {
        makeObservable<
            SearchCriteriaStore<T>,
            '_criteriaOptions' | '_searchCriteria' | '_isUniqueSelected' | '_defaultSearchCriteriaLength'
        >(this, {
            _criteriaOptions: observable,
            _searchCriteria: observable,
            _isUniqueSelected: observable,
            _defaultSearchCriteriaLength: observable,
            searchCriteria: computed,
            isAddCriterionDisabled: computed,
            filteredOptions: computed,
            nonPrimaryOptionsFiltered: computed,
            setCriterionOption: action,
            setIsUniqueSelected: action,
            deleteCriterion: action,
            addCriterion: action,
            atMaxCriteria: computed,
            setCriterionExactMatch: action,
            applyCriteriaFromObject: action,
        })
        this._criteriaOptions = [...criteriaOptionsArray]
        this._searchCriteria.push(...searchCriteriaArray.map((value) => ({ ...value, uniqueId: uuidv4() })))
        this.maxCriteria = maxCriteria
        this.searchOptionType = searchOptionType
        this.requiredCriteria = requiredCriteria
        this.primaryFilterNames = primaryFilterNames ? primaryFilterNames : []
        this._defaultSearchCriteriaLength = searchCriteriaArray.length
    }

    get searchCriteria(): Array<SearchCriterion<T>> {
        return [...this._searchCriteria]
    }

    /** Every option a row can pick from — the keys a link can carry. */
    get criteriaOptions(): Array<SelectOptionHide<T>> {
        return this._criteriaOptions
    }

    get isAddCriterionDisabled(): boolean {
        return this._isUniqueSelected || this.atMaxCriteria
    }

    get filteredOptions(): Array<SelectOptionHide<T>> {
        const currentSelectedOptionIds = this.searchCriteria.map((searchCriterion) => searchCriterion.selectedOption.id)
        return this._criteriaOptions.filter((option) => !currentSelectedOptionIds.includes(option.id))
    }

    get nonPrimaryOptionsFiltered(): Array<SelectOptionHide<T>> {
        return this.filteredOptions.filter((option) => !option.isPrimary)
    }

    get atMaxCriteria(): boolean {
        return this._searchCriteria.length >= this.maxCriteria
    }

    get isUniqueSelected(): boolean {
        return this._isUniqueSelected
    }

    get defaultSearchCriteriaLength(): number {
        return this._defaultSearchCriteriaLength
    }

    isPrimaryFilter(value: string): boolean {
        return this.primaryFilterNames.includes(value)
    }

    isSomeCriteria(searchCriteria: Array<SearchCriterion<T>>): boolean {
        return searchCriteria.some((criterion) => criterion.searchTerm && criterion.searchTerm.trim().length > 0)
    }

    isCriteriaSearchReady(searchCriteria: Array<SearchCriterion<T>>, filters?: object): boolean {
        switch (this.requiredCriteria) {
            case RequiredCriteria.all:
                return searchCriteria.every(
                    (criterion) => criterion.searchTerm && criterion.searchTerm.trim().length > 0
                )

            case RequiredCriteria.some:
                return this.isSomeCriteria(searchCriteria)

            case RequiredCriteria.first:
                return !!(
                    searchCriteria.length &&
                    searchCriteria[0].searchTerm &&
                    searchCriteria[0].searchTerm.trim().length > 0
                )

            case RequiredCriteria.primaryFilter:
                return (
                    (!!filters &&
                        Object.entries(filters).some((filter) => this.isPrimaryFilter(filter[0]) && filter[1])) ||
                    this.isSomeCriteria(searchCriteria)
                )

            case RequiredCriteria.none:
                return true
        }
    }

    getOptionsForCriterion(criterion: SearchCriterion<T>): Array<SelectOptionHide<T>> {
        switch (this.searchOptionType) {
            case SearchOptionType.primary:
                if (criterion.id === this.searchCriteria[0].id) {
                    return this.getFilteredOptionsPlusSelected(criterion)
                }
                return this.getFilteredOptionsPlusSelected(criterion).filter((option) => !option.isPrimary)
            case SearchOptionType.filter:
                return this.getFilteredOptionsPlusSelected(criterion)
            case SearchOptionType.determine:
                return this.determineOptions(criterion)
        }
    }

    determineOptions(criterion: SearchCriterion<T>): Array<SelectOptionHide<T>> {
        if (criterion.isDisabled) {
            const criterionOption = this._criteriaOptions.find(
                (option) => option.value === criterion.selectedOption.value
            )
            return [
                {
                    id: '1',
                    value: criterion.selectedOption.value,
                    label: criterionOption ? criterionOption.label : '',
                },
            ]
        } else {
            return this._criteriaOptions.filter((option) => !option.shouldHide)
        }
    }

    getFilteredOptionsPlusSelected(criterion: SearchCriterion<T>): Array<SelectOptionHide<T>> {
        const availableOptions = [...this.filteredOptions]
        if (criterion.selectedOption) {
            availableOptions.push(criterion.selectedOption)
        }
        return availableOptions
    }

    setIsUniqueSelected(isSelected: boolean): void {
        this._isUniqueSelected = isSelected
    }

    setCriterionOption(idx: number, selectedOption: Maybe<SelectOptionHide<T>>, searchTerm?: string): void {
        if (selectedOption) {
            this._searchCriteria[idx] = {
                ...this._searchCriteria[idx],
                id: uuidv4(),
                searchTerm: searchTerm ?? '',
                selectedOption: selectedOption,
                exactMatchOnly: false,
            }
            if (selectedOption.isUnique) {
                this.deleteCriterion(idx + 1, this._searchCriteria.length - (idx + 1))
                this.setIsUniqueSelected(true)
            } else {
                this.setIsUniqueSelected(false)
            }
        }
    }

    setCriterionExactMatch(idx: number, isExactMatch: boolean): void {
        this._searchCriteria[idx] = {
            ...this._searchCriteria[idx],
            exactMatchOnly: isExactMatch,
        }
    }

    deleteCriterion(idx: number, deleteCount = 1): void {
        this._searchCriteria.splice(idx, deleteCount)
    }

    addCriterion(): void {
        let nextCriterionOption: SelectOptionHide<T>
        switch (this.searchOptionType) {
            case SearchOptionType.primary:
                nextCriterionOption = this.nonPrimaryOptionsFiltered[0]
                break
            case SearchOptionType.filter:
                nextCriterionOption = this.filteredOptions[0]
                break
            case SearchOptionType.determine:
                nextCriterionOption = {
                    // 2 is the index for writer
                    ...this._criteriaOptions[2],
                    id: uuidv4(),
                }
                break
        }
        this._searchCriteria.push({
            id: nextCriterionOption.id,
            searchTerm: '',
            selectedOption: nextCriterionOption,
        })
    }

    /**
     * The inverse of `mapCriteriaToObject`, for restoring a search from a link. Unknown or blank terms are
     * dropped and the store's rules hold (primary options only in the first row, a unique option alone,
     * never past the maximum). Run it before the form first renders: the form syncs its rows from here.
     */
    applyCriteriaFromObject(values: Record<string, unknown>): void {
        Object.entries(values).forEach(([key, rawValue]) => {
            const term = (Array.isArray(rawValue) ? rawValue[0] : rawValue) as unknown
            if (typeof term !== 'string' || term.trim().length === 0) {
                return
            }
            const option = this._criteriaOptions.find((candidate) => candidate.value === key)
            const exactOption = this._criteriaOptions.find(
                (candidate) => `${candidate.value}${EXACT_MATCH_SUFFIX}` === key
            )
            const target = option ?? exactOption
            if (target) {
                this.placeCriterion(target, term, !option && !!exactOption?.exactMatchOnly)
            }
        })
    }

    private isOptionAllowedAtRow(option: SelectOptionHide<T>, rowIdx: number): boolean {
        if (this.searchOptionType === SearchOptionType.primary) {
            return rowIdx === 0 || !option.isPrimary
        }
        return this.searchOptionType === SearchOptionType.filter
    }

    private placeCriterion(option: SelectOptionHide<T>, term: string, isExact: boolean): void {
        const existingIdx = this._searchCriteria.findIndex((row) => row.selectedOption.id === option.id)
        if (existingIdx >= 0) {
            this._searchCriteria[existingIdx] = {
                ...this._searchCriteria[existingIdx],
                searchTerm: term,
                exactMatchOnly: isExact,
            }
            return
        }

        // A disabled row is a fixed field, so it is never repurposed for another option.
        const emptyIdx = this._searchCriteria.findIndex((row) => !row.searchTerm && !row.isDisabled)
        if (emptyIdx >= 0 && this.isOptionAllowedAtRow(option, emptyIdx)) {
            this.setCriterionOption(emptyIdx, option, term)
            if (isExact) {
                this.setCriterionExactMatch(emptyIdx, true)
            }
            return
        }

        const newIdx = this._searchCriteria.length
        if (
            !this.atMaxCriteria &&
            !this._isUniqueSelected &&
            !option.isUnique &&
            this.isOptionAllowedAtRow(option, newIdx)
        ) {
            this._searchCriteria.push({
                id: option.id,
                searchTerm: term,
                selectedOption: option,
                exactMatchOnly: isExact,
            })
        }
    }

    // This only syncs fields on this observable, and will not cause additional re-renders
    syncFormValues(data: Array<SearchCriterion<T>>) {
        data &&
            data.forEach((criteria, idx) => {
                if (this._searchCriteria.length > idx) {
                    this._searchCriteria[idx].searchTerm = criteria.searchTerm
                }
            })
    }
}
